import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import Header from "../components/Header";
import { useAuth } from "../context/AuthContext";
import { meusConvites, vagasRecomendadas, caminhoVaga } from "../services/api";

function corMatch(m) { return m >= 70 ? "bg-green-100 text-green-800" : m >= 40 ? "bg-yellow-100 text-yellow-800" : "bg-gray-100 text-gray-600"; }
function dataBr(x) { try { return new Date(x).toLocaleDateString("pt-BR", { day: "2-digit", month: "short" }); } catch { return ""; } }

export default function OportunidadesPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [convites, setConvites] = useState(null);
  const [matches, setMatches] = useState(null);
  const [erro, setErro] = useState(null);

  useEffect(() => {
    if (!user || user.tipo !== 0) return;
    meusConvites().then(setConvites).catch((e) => setErro(e.message));
    vagasRecomendadas().then((r) => setMatches(r)).catch(() => setMatches([]));
  }, [user]);

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="max-w-4xl mx-auto px-4 md:px-6 py-8 pb-28 md:pb-8">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-text">✨ Oportunidades</h1>
          <p className="text-gray-600 text-sm mt-1">Empresas que te procuraram e vagas que combinam com o seu perfil — tudo num lugar só.</p>
        </div>

        {!user && <Aviso>Faça <Link to="/entrar" className="text-primary font-semibold">login</Link> para ver suas oportunidades.</Aviso>}
        {user?.tipo === 1 && <Aviso>Esta área é do candidato. Sua conta é de empresa — veja o <Link to="/painel" className="text-primary font-semibold">painel</Link>.</Aviso>}
        {erro && <p className="mt-4 text-red-600">{erro}</p>}

        {user?.tipo === 0 && (
          <>
            {/* ---- Empresas que te procuraram ---- */}
            <section className="mt-6">
              <h2 className="text-lg font-bold text-gray-900 mb-3">Empresas que te procuraram</h2>
              {!convites && !erro && <p className="text-gray-500">Carregando...</p>}
              {convites && convites.length === 0 && (
                <div className="rounded-2xl border border-dashed border-gray-300 bg-white p-6 text-center text-sm text-gray-500">
                  Nenhuma empresa te procurou ainda. Mantenha seu <Link to="/curriculo" className="text-primary font-semibold underline">currículo</Link> completo e atualizado para aparecer nas buscas das empresas.
                </div>
              )}
              <div className="space-y-2">
                {convites?.map((c) => (
                  <div key={c.id} className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-gray-900">
                          {c.vagaId
                            ? <>Convite para a vaga <span className="text-primary">{c.vagaTitulo || "(vaga)"}</span></>
                            : <>Interesse no seu perfil</>}
                        </div>
                        <div className="text-sm text-gray-500">{c.empresaNome} · {dataBr(c.criadoEm)}</div>
                        {c.mensagem && <p className="mt-2 text-sm text-gray-700 whitespace-pre-line border-l-2 border-gray-200 pl-3">{c.mensagem}</p>}
                      </div>
                      <div className="flex flex-col gap-1.5 shrink-0">
                        {c.vagaId && c.vagaAberta && (
                          <button onClick={() => navigate(`/vagas/${c.vagaId}`)} className="rounded-xl bg-orange-600 hover:bg-orange-700 text-white font-semibold px-4 py-2 text-sm">Ver vaga e me candidatar</button>
                        )}
                        {c.vagaId && !c.vagaAberta && <span className="text-xs text-gray-400 italic">vaga encerrada</span>}
                        <button onClick={() => navigate(`/empresas/${c.empresaId}`)} className="rounded-xl border border-gray-300 text-gray-600 font-semibold px-4 py-2 text-sm hover:bg-gray-50">Ver empresa</button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* ---- Vagas com match ---- */}
            <section className="mt-8">
              <div className="flex items-center justify-between gap-2 mb-3">
                <h2 className="text-lg font-bold text-gray-900">Vagas que combinam com você</h2>
                <Link to="/vagas" className="text-primary text-sm font-semibold hover:underline">Ver todas →</Link>
              </div>
              {!matches && <p className="text-gray-500">Carregando...</p>}
              {matches && matches.length === 0 && (
                <div className="rounded-2xl border border-dashed border-gray-300 bg-white p-6 text-center text-sm text-gray-500">
                  Nenhuma vaga com match ainda. <Link to="/curriculo" className="text-primary font-semibold underline">Complete seu currículo</Link> (área, cidade, senioridade, skills) para encontrar vagas alinhadas ao seu perfil.
                </div>
              )}
              <div className="space-y-2">
                {matches?.slice(0, 8).map((r) => (
                  <div key={r.vaga.id} onClick={() => navigate(caminhoVaga(r.vaga))} className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm flex flex-wrap items-center gap-3 cursor-pointer hover:bg-gray-50">
                    <span className={`font-bold rounded-full px-2.5 py-1 text-xs ${corMatch(r.match)}`}>{r.match}%</span>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-gray-900">{r.vaga.titulo}{r.vaga.remoto && <span className="ml-2 text-[11px] text-green-700">remota</span>}</div>
                      <div className="text-sm text-gray-500">{r.vaga.empresa} · {r.vaga.cidade || (r.vaga.remoto ? "Remota" : "—")}{r.vaga.senioridade ? " · " + r.vaga.senioridade : ""}</div>
                      {r.motivos?.length > 0 && <div className="text-xs text-gray-400 mt-0.5">match por: {r.motivos.slice(0, 3).join(", ")}</div>}
                    </div>
                    <span className="text-primary text-sm font-semibold shrink-0">Ver vaga →</span>
                  </div>
                ))}
              </div>
            </section>
          </>
        )}
      </main>
    </div>
  );
}

function Aviso({ children }) {
  return <div className="mt-6 rounded-2xl border border-gray-200 bg-white p-5 text-gray-600">{children}</div>;
}
